<template>
  <div class="login-container">
    <div class="login-background">
      <div class="gradient-overlay"></div>
      <div class="noise-overlay"></div>
    </div>

    <div class="login-content">
      <!-- 左侧通道对比 -->
      <div class="comparison-panel">
        <div class="panel-header">
          <h2><i class="fas fa-road"></i> {{ $t('manga.login.channelCompare') }}</h2>
          <p>{{ $t('manga.login.channelDesc') }}</p>
        </div>

        <div class="comparison">
          <div class="queue-type free">
            <h3 class="queue-title free"><i class="fas fa-users"></i> {{ $t('manga.login.freeQueue') }}</h3>
            <p style="color: rgba(255, 255, 255, 0.8); margin-bottom: 15px;">{{ $t('manga.login.freeQueueDesc') }}</p>

            <ul class="feature-list">
              <li v-for="(item, i) in $tm('manga.login.freeItems')" :key="i"><i :class="i < 2 ? 'fas fa-check' : 'fas fa-times'"></i> {{ item }}</li>
            </ul>
          </div>

          <div class="queue-type vip">
            <h3 class="queue-title vip"><i class="fas fa-crown"></i> {{ $t('manga.login.vipQueue') }}</h3>
            <p style="color: rgba(255, 255, 255, 0.8); margin-bottom: 15px;">{{ $t('manga.login.vipQueueDesc') }}</p>

            <ul class="feature-list">
              <li v-for="(item, i) in $tm('manga.login.vipItems')" :key="i"><i class="fas fa-check"></i> {{ item }}</li>
            </ul>
          </div>
        </div>

        <div class="usage-tip">
          <h4><i class="fas fa-lightbulb"></i> {{ $t('manga.login.usageTipTitle') }}</h4>
          <p>{{ $t('manga.login.usageTipDesc') }}</p>
        </div>
      </div>

      <!-- 右侧登录卡片 -->
      <div class="login-card">
        <div class="login-header">
          <div class="logo-section">
            <img src="@/assets/logo.png" alt="Koke Logo" class="logo-img" />
            <h1 class="brand-title">KokeManga</h1>
          </div>
          <h2 class="login-title">{{ isLoginMode ? $t('manga.login.welcomeBack') : $t('manga.login.createAccount') }}</h2>
          <p class="login-subtitle">{{ isLoginMode ? $t('manga.login.loginSubtitle') : $t('manga.login.registerSubtitle') }}</p>
        </div>

        <el-form class="login-form" :model="form" :rules="formRules" ref="formRef">
          <el-form-item prop="email">
            <el-input v-model="form.email" :placeholder="$t('manga.login.email')" clearable class="custom-input">
              <template #prefix>
                <i class="fas fa-envelope"></i>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item prop="password">
            <el-input v-model="form.password" type="password" :placeholder="$t('manga.login.password')" clearable @keyup.enter="handleSubmit"
              class="custom-input">
              <template #prefix>
                <i class="fas fa-lock"></i>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item v-if="!isLoginMode" prop="confirmPassword">
            <el-input v-model="form.confirmPassword" type="password" :placeholder="$t('manga.login.confirmPassword')" clearable
              @keyup.enter="handleSubmit" class="custom-input">
              <template #prefix>
                <i class="fas fa-lock"></i>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item class="submit-item">
            <el-button class="submit-btn" type="primary" @click="handleSubmit" :loading="loading">
              {{ isLoginMode ? $t('manga.login.loginBtn') : $t('manga.login.registerBtn') }}
            </el-button>
          </el-form-item>

          <div class="form-footer">
            <el-button class="switch-btn" @click="toggleMode" type="text">
              {{ isLoginMode ? $t('manga.login.noAccount') : $t('manga.login.hasAccount') }}
            </el-button>

            <el-button class="forgot-btn" @click="handleForgotPassword" type="text">
              {{ $t('manga.login.forgotPassword') }}
            </el-button>
          </div>
        </el-form>

        <div class="agreement-text">
          <p class="agreement-desc">
            {{ isLoginMode ? $t('manga.login.loginAgreement') : $t('manga.login.registerAgreement') }}
            <el-button type="text" class="agreement-link" @click="showModal('agreement')">{{ $t('manga.translate.agreement') }}</el-button>
            {{ $t('manga.login.and') }}
            <el-button type="text" class="agreement-link" @click="showModal('privacy')">{{ $t('manga.login.privacyPolicy') }}</el-button>
          </p>
        </div>
      </div>
    </div>

    <!-- 模态框组件 -->
    <ComicModal :isVisible="modalVisible" :title="modalTitle" :modalType="modalType" @close="closeModal" />
  </div>
</template>

<script>
import { login, register } from '@/api/auth.js'
import { setToken } from '@/utils/auth.js'
import { localPath } from '@/i18n'
import ComicModal from '@/components/manga/ComicModal.vue'

export default {
  components: {
    ComicModal
  },
  props: {},
  data() {
    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.login.emailRequired')))
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        callback(new Error(this.$t('manga.login.emailInvalid')))
      } else {
        callback()
      }
    }

    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.login.passwordRequired')))
      } else if (value.length < 6) {
        callback(new Error(this.$t('manga.login.passwordMin')))
      } else {
        callback()
      }
    }

    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.login.confirmRequired')))
      } else if (value !== this.form.password) {
        callback(new Error(this.$t('manga.login.confirmMismatch')))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      isLoginMode: true,
      modalVisible: false,
      modalType: '',
      modalTitle: '',
      form: {
        email: '',
        password: '',
        confirmPassword: ''
      },
      formRules: {
        email: [{ validator: validateEmail, trigger: 'blur' }],
        password: [{ validator: validatePassword, trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
      }
    }
  },
  created() {
  },
  methods: {
    toggleMode() {
      this.isLoginMode = !this.isLoginMode
      this.form.confirmPassword = ''
      this.$refs.formRef?.resetFields()
    },

    handleForgotPassword() {
      this.$router.push(localPath('/resetPassword'))
    },

    showModal(type) {
      this.modalType = type
      this.modalTitle = type === 'agreement' ? this.$t('manga.login.agreementTitle') : this.$t('manga.login.privacyTitle')
      this.modalVisible = true
    },

    closeModal() {
      this.modalVisible = false
      this.modalType = ''
      this.modalTitle = ''
    },

    async handleSubmit() {
      this.$refs.formRef.validate(async valid => {
        if (valid) {
          try {
            this.loading = true

            if (this.isLoginMode) {
              const { data } = await login({
                username: this.form.email,
                password: this.form.password
              })
              this.$message.success(this.$t('manga.login.loginSuccess'))
              setToken(data)
              this.$router.push(localPath('/vip'))
            } else {
              await register({
                email: this.form.email,
                password: this.form.password
              })
              this.$message.success(this.$t('manga.login.registerSuccess'))
              this.isLoginMode = true
              this.form.confirmPassword = ''
            }
          } catch (e) {
            console.error(this.isLoginMode ? 'login failed:' : 'register failed:', e)
            this.$message.error(this.isLoginMode ? this.$t('manga.login.loginFailed') : this.$t('manga.login.registerFailed'))
          } finally {
            this.loading = false
          }
        }
      })
    }
  }
}</script>

<style scoped>
.login-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #0a0f1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('~@/assets/images/login.jpg') no-repeat center center;
  background-size: cover;
  z-index: 1;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(10, 15, 26, 0.92) 0%, rgba(15, 23, 42, 0.88) 50%, rgba(30, 41, 59, 0.85) 100%);
}

.noise-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
}

.login-content {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1100px;
  display: flex;
  gap: 0;
  align-items: stretch;
}

/* 左侧通道对比面板 */
.comparison-panel {
  flex: 3;
  background: rgba(30, 41, 59, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-right: none;
  border-radius: 20px 0 0 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.panel-header {
  margin-bottom: 1.5rem;
}

.panel-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #5eead4;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-header h2 i {
  color: #fbbf24;
}

.panel-header p {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0;
}

/* 对比内容 */
.comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  flex: 1;
}

.queue-type {
  border-radius: 12px;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.queue-type.free {
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.queue-type.free:hover {
  border-color: rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.6);
}

.queue-type.vip {
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.12), rgba(94, 234, 212, 0.05));
  border: 1px solid rgba(167, 139, 250, 0.25);
}

.queue-type.vip:hover {
  border-color: rgba(167, 139, 250, 0.4);
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.18), rgba(94, 234, 212, 0.08));
}

.queue-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.queue-title.free {
  color: #94a3b8;
}

.queue-title.vip {
  color: #a78bfa;
}

.feature-list {
  list-style: none;
  margin: 8px 0 0 0;
  padding: 0;
}

.feature-list li {
  padding: 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #cbd5e1;
}

.feature-list i {
  width: 16px;
  font-size: 0.8rem;
}

.feature-list i.fa-check {
  color: #5eead4;
}

.feature-list i.fa-times {
  color: #f87171;
}

.usage-tip {
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.usage-tip i {
  color: #5eead4;
  font-size: 1rem;
  margin-top: 2px;
}

.usage-tip h4 {
  color: #5eead4;
  font-size: 0.9rem;
  margin: 0 0 0.4rem 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.usage-tip p {
  color: #cbd5e1;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

/* 右侧登录卡片 */
.login-card {
  flex: 1.5;
  background: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-left: 1px solid rgba(94, 234, 212, 0.08);
  border-radius: 0 20px 20px 0;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-header {
  text-align: center;
  margin-bottom: 1.8rem;
}

.logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1rem;
}

.logo-img {
  height: 2rem;
  width: auto;
  filter: drop-shadow(0 2px 10px rgba(94, 234, 212, 0.3));
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  letter-spacing: 1px;
}

.login-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.3rem 0;
}

.login-subtitle {
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
}

.login-form {
  margin-bottom: 1rem;
}

.custom-input {
  margin-bottom: 0.8rem;
}

:deep(.custom-input .el-input__wrapper) {
  background: rgba(30, 41, 59, 0.6) !important;
  border: 1px solid rgba(94, 234, 212, 0.12) !important;
  border-radius: 12px !important;
  padding: 4px 12px !important;
  transition: all 0.3s ease !important;
  box-shadow: none !important;
}

:deep(.custom-input .el-input__wrapper:hover) {
  border-color: rgba(94, 234, 212, 0.3) !important;
}

:deep(.custom-input .el-input__wrapper.is-focus) {
  border-color: #5eead4 !important;
  box-shadow: 0 0 0 3px rgba(94, 234, 212, 0.08) !important;
}

:deep(.custom-input .el-input__inner) {
  font-size: 0.9rem !important;
  color: #e2e8f0 !important;
}

:deep(.custom-input .el-input__inner::placeholder) {
  color: #64748b !important;
}

:deep(.custom-input .el-input__prefix) {
  color: #5eead4 !important;
}

.submit-item {
  margin-bottom: 0.5rem !important;
}

.submit-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(135deg, #5eead4, #a78bfa) !important;
  border: none !important;
  border-radius: 12px !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  color: #0a0f1a !important;
  transition: all 0.3s ease !important;
  letter-spacing: 2px;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(94, 234, 212, 0.25) !important;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.8rem;
  padding-top: 0.8rem;
}

.switch-btn {
  color: #5eead4 !important;
  font-size: 0.78rem !important;
  padding: 0 !important;
  height: auto !important;
}

.switch-btn:hover {
  color: #a78bfa !important;
  background: none !important;
}

.forgot-btn {
  color: #64748b !important;
  font-size: 0.78rem !important;
  padding: 0 !important;
  height: auto !important;
}

.forgot-btn:hover {
  color: #5eead4 !important;
  background: none !important;
}

.agreement-text {
  margin-top: 1rem;
  padding-top: 0.8rem;
  border-top: 1px solid rgba(94, 234, 212, 0.08);
}

.agreement-desc {
  font-size: 0.7rem;
  color: #64748b;
  text-align: center;
  line-height: 1.4;
  margin: 0;
}

.agreement-link {
  color: #5eead4 !important;
  font-size: 0.7rem !important;
  padding: 0 !important;
  height: auto !important;
}

.agreement-link:hover {
  color: #a78bfa !important;
  background: none !important;
  text-decoration: underline;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-container {
    padding: 1rem;
  }

  .login-content {
    flex-direction: column;
    max-width: 500px;
  }

  .login-card {
    order: -1;
    border-left: 1px solid rgba(94, 234, 212, 0.15);
    border-bottom: none;
    border-radius: 20px 20px 0 0;
    padding: 1.5rem;
  }

  .comparison-panel {
    flex: none;
    border-right: 1px solid rgba(94, 234, 212, 0.15);
    border-top: none;
    border-radius: 0 0 20px 20px;
    padding: 1.5rem;
  }

  .comparison {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 0.5rem;
  }

  .comparison-panel {
    padding: 1.2rem;
  }

  .login-card {
    padding: 1.2rem;
  }

  .brand-title {
    font-size: 1.3rem;
  }

  .login-title {
    font-size: 1.2rem;
  }

  .form-footer {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 0.5rem;
  }

  .comparison-panel {
    padding: 1rem;
  }

  .channel-cards {
    flex-direction: column;
  }

  .login-card {
    padding: 1.2rem;
  }

  .brand-title {
    font-size: 1.3rem;
  }

  .login-title {
    font-size: 1.2rem;
  }

  .form-footer {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}
</style>
